import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PlayArrow,
  VideoLibrary,
  Timer,
  TrendingUp,
  History,
  Psychology,
} from '@mui/icons-material';

const CleanCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(255, 255, 255, 0.05) inset'
    : '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.9) inset',
  backdropFilter: 'blur(12px) saturate(120%)',
  marginBottom: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 0 rgba(255, 255, 255, 0.08) inset'
      : '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 0 rgba(255, 255, 255, 0.95) inset',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: 8,
}));

const CounterText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.2rem',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  lineHeight: 1.1,
  color: theme.palette.text.primary, // Fallback for browsers that don't support gradient text
}));

const StatsCard = ({ videoCount, totalVideos, playStats }) => {
  const watchedPercentage = Math.min((videoCount / totalVideos) * 100, 100);
  const sessionTime = Math.floor(videoCount * 4.2); // Estimate ~4.2 min per vlog

  return (
    <CleanCard elevation={0}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <TrendingUp sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" color="text.primary" fontWeight={600}>
            Session Stats
          </Typography>
        </Box>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={6}>
            <StatItem>
              <PlayArrow sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
              <CounterText>{videoCount}</CounterText>
              <Typography variant="body2" color="text.secondary">
                Videos Watched
              </Typography>
            </StatItem>
          </Grid>
          <Grid item xs={6}>
            <StatItem>
              <VideoLibrary sx={{ color: 'secondary.main', fontSize: 32, mb: 1 }} />
              <CounterText>{totalVideos}+</CounterText>
              <Typography variant="body2" color="text.secondary">
                Total Available
              </Typography>
            </StatItem>
          </Grid>
        </Grid>

        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Collection Progress
            </Typography>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {watchedPercentage.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={watchedPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: 4,
              },
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Timer sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Estimated watch time: <strong style={{ color: 'inherit' }}>{sessionTime} minutes</strong>
          </Typography>
        </Box>

        {/* Play History Stats */}
        {playStats && playStats.totalPlays > 0 && (
          <Box 
            mt={2} 
            p={2} 
            borderRadius={2} 
            sx={(theme) => ({
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(102, 126, 234, 0.15)' 
                : 'rgba(102, 126, 234, 0.08)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(102, 126, 234, 0.3)' 
                : 'rgba(102, 126, 234, 0.2)'}`
            })}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <History sx={{ color: '#667eea', fontSize: 20 }} />
              <Typography variant="body2" sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 600
              })}>
                Play History
              </Typography>
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={(theme) => ({
                  color: theme.palette.text.secondary
                })}>
                  Total Plays
                </Typography>
                <Typography variant="body2" sx={(theme) => ({
                  color: theme.palette.text.primary,
                  fontWeight: 700
                })}>
                  {playStats.totalPlays}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={(theme) => ({
                  color: theme.palette.text.secondary
                })}>
                  Unique Videos
                </Typography>
                <Typography variant="body2" sx={(theme) => ({
                  color: theme.palette.text.primary,
                  fontWeight: 700
                })}>
                  {playStats.uniqueVideosPlayed} ({playStats.percentageWatched}%)
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}


      </CardContent>
    </CleanCard>
  );
};

export default StatsCard;